import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Analytics } from "@vercel/analytics/react";
import Sequencer from "./components/sequencer/index";
import PerformanceSection from "./components/sequencer/components/PerformanceSection";
import MidiExportSection from "./components/sequencer/components/MidiExportSection";
import RegisterKeyboardSection from "./components/sequencer/components/RegisterKeyboardSection";
import { RegisterProvider } from "./context/RegisterContext";
import SampleAndFXSection from "./components/SampleAndFXSection";
import MasterPresetSelector from "./components/MasterPresetSelector";
import {
  getDefaultInstrument,
  getMultiKeyInstrumentById,
} from "./utils/sampleUtils";
import { initializeToneContext } from "./utils/audioUtils";
import {
  applyMasterPreset,
  MasterPresetCallbacks,
} from "./utils/masterPresets";
import { getPresetById } from "./utils/presetStorage";
import {
  hasProgressionChanged,
  resolvePresetProgressionState,
} from "./utils/presetProgression";
import { CurrentStateRefs } from "./utils/presetCapture";
import { usePerformanceActions } from "./context/PerformanceContext";
import { useInstrumentSelection } from "./hooks/useInstrumentSelection";
import { useSampleEngine } from "./hooks/useSampleEngine";
import {
  SequencerHandle,
  SequencerProgressionState,
} from "./components/sequencer/types";
import * as Tone from "tone";

function App() {
  const defaultMasterPreset = getPresetById("default");
  const initialInstrument = defaultMasterPreset
    ? getMultiKeyInstrumentById(defaultMasterPreset.sample.id) ||
      getDefaultInstrument()
    : getDefaultInstrument();
  const [bpm, setBpm] = useState(defaultMasterPreset?.sequencer.bpm ?? 120);
  const {
    currentInstrument,
    setInstrumentById,
    handleInstrumentChange,
    handlePreviousInstrument,
    handleNextInstrument,
    handleShuffle,
  } = useInstrumentSelection(initialInstrument);
  const [error, setError] = useState<string | null>(null);

  // Master preset state
  const [selectedMasterPreset, setSelectedMasterPreset] =
    useState<string>("default");

  // Sequencer state for master presets
  const [selectedKey, setSelectedKey] = useState<string>(
    defaultMasterPreset?.chord.key ?? "C",
  );
  const [selectedProgression, setSelectedProgression] = useState<string>(
    defaultMasterPreset?.chord.progression ?? "melancholic",
  );
  const [currentProgressionState, setCurrentProgressionState] =
    useState<SequencerProgressionState | null>(() =>
      resolvePresetProgressionState(defaultMasterPreset),
    );

  // Audio-related state
  // Track play intent so we can auto-start once samples finish loading.
  const pendingStartRef = useRef<boolean>(false);
  const hasAppliedDefaultPresetRef = useRef<boolean>(false);
  const presetApplyPromiseRef = useRef<Promise<void> | null>(null);
  const sequencerRef = useRef<SequencerHandle | null>(null);

  const [isSequencerPlaying, setIsSequencerPlaying] = useState(false);
  const [activeKeyboardNotes, setActiveKeyboardNotes] = useState<string[]>([]);
  const { sampler, isSamplerLoaded, waveformData, sampleDuration } =
    useSampleEngine({
      currentInstrument,
      setError,
    });

  const awaitCurrentPresetApplication = useCallback(async () => {
    if (presetApplyPromiseRef.current) {
      await presetApplyPromiseRef.current;
    }
  }, []);

  // FX mapping state
  const [mappingMode, setMappingMode] = useState<string | null>(null);
  const [fxModes, setFxModes] = useState<{
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }>(
    defaultMasterPreset?.fxModes || {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
  );

  // UI refresh trigger for FX controls (incremented when preset is applied)
  const [fxRefreshTrigger, setFxRefreshTrigger] = useState<number>(0);

  const { setStrum, setGroove, getStrum, getGroove } = usePerformanceActions();

  const handleTogglePlay = useCallback(async () => {
    if (isSequencerPlaying) {
      pendingStartRef.current = false;
      sequencerRef.current?.stop();
      return;
    }

    pendingStartRef.current = true;

    try {
      await awaitCurrentPresetApplication();
      await initializeToneContext();
      setError(null);
    } catch (err) {
      console.error("Error initializing audio:", err);
      setError("Failed to initialize audio. Please try again.");
      pendingStartRef.current = false;
      return;
    }

    if (!isSamplerLoaded) return;

    const isPrepared = await (sequencerRef.current?.prepare() ??
      Promise.resolve(false));
    if (!isPrepared) {
      setError("Audio engine is still warming up. Please try play again.");
      pendingStartRef.current = false;
      return;
    }

    pendingStartRef.current = false;
    await sequencerRef.current?.start();
  }, [awaitCurrentPresetApplication, isSamplerLoaded, isSequencerPlaying]);

  // Update BPM when it changes
  useEffect(() => {
    Tone.Transport.bpm.value = bpm;
  }, [bpm]);

  useEffect(() => {
    if (!pendingStartRef.current || isSequencerPlaying || !isSamplerLoaded) {
      return;
    }

    pendingStartRef.current = false;

    const startPendingSequencer = async () => {
      try {
        await awaitCurrentPresetApplication();
        await initializeToneContext();
        setError(null);
        const isPrepared = await (sequencerRef.current?.prepare() ??
          Promise.resolve(false));
        if (!isPrepared) {
          setError("Audio engine is still warming up. Please try play again.");
          return;
        }
        await sequencerRef.current?.start();
      } catch (err) {
        console.error("Error initializing audio:", err);
        setError("Failed to initialize audio. Please try again.");
      }
    };

    void startPendingSequencer();
  }, [awaitCurrentPresetApplication, isSamplerLoaded, isSequencerPlaying]);

  const handleIsPlayingChange = useCallback((isPlaying: boolean) => {
    setIsSequencerPlaying(isPlaying);
  }, []);

  // Master preset handlers
  const handleMasterPresetChange = useCallback(
    (presetId: string) => {
      if (!presetId) {
        setSelectedMasterPreset("");
        setCurrentProgressionState(null);
        return;
      }

      const preset = getPresetById(presetId);
      if (!preset) {
        console.error(`Master preset not found: ${presetId}`);
        return;
      }

      setSelectedMasterPreset(presetId);
      setCurrentProgressionState(resolvePresetProgressionState(preset));

      // Create callbacks object for applying the preset
      const callbacks: MasterPresetCallbacks = {
        onSampleChange: (instrumentId: string) => {
          setInstrumentById(instrumentId);
        },
        onBpmChange: (newBpm: number) => {
          setBpm(newBpm);
        },
        onKeyChange: (key: string) => {
          setSelectedKey(key);
          sequencerRef.current?.setKey(key);
        },
        onProgressionChange: (progression: string) => {
          setSelectedProgression(progression);
          sequencerRef.current?.setProgression(progression);
        },
        onCustomProgressionChange: (progression) => {
          setSelectedProgression(progression.name || "custom");
          sequencerRef.current?.setProgression(progression);
        },
        onPatternPresetChange: (presetKey: string) => {
          sequencerRef.current?.applyPatternPreset(presetKey);
        },
        onStepsCountChange: (stepsCount: number) => {
          sequencerRef.current?.setStepsCount(stepsCount);
        },
        onStepPatternChange: (stepPattern) => {
          sequencerRef.current?.setPattern(stepPattern);
        },
        onRegisterChange: (register) => {
          sequencerRef.current?.setRegister(register);
        },
        onRibbonPositionsChange: (
          ribbonPositions: Array<{ startPercent: number; endPercent: number }>,
        ) => {
          sequencerRef.current?.setRibbonPositions(ribbonPositions);
        },
        onFxModesChange: (fxModes: {
          filter: boolean;
          reverb: boolean;
          delay: boolean;
          reverse: boolean;
        }) => {
          setFxModes(fxModes);
        },
        onSequencerModeChange: (mode: "chord" | "drone-mono" | "drone-duo") => {
          sequencerRef.current?.setSequencerMode(mode);
        },
        onDroneNotesChange: (droneNotes: string[]) => {
          sequencerRef.current?.setDroneNotes(droneNotes);
        },
        onStrumChange: (newStrum: number) => {
          setStrum(newStrum);
        },
        onGrooveChange: (newGroove: number) => {
          setGroove(newGroove);
        },
      };

      // Apply the master preset and keep a promise so playback can await completion.
      const applyPromise = applyMasterPreset(preset, callbacks)
        .catch((err) => {
          console.error("Failed to apply master preset:", err);
        })
        .finally(() => {
          setFxRefreshTrigger((prev) => prev + 1);
          if (presetApplyPromiseRef.current === applyPromise) {
            presetApplyPromiseRef.current = null;
          }
        });

      presetApplyPromiseRef.current = applyPromise;
    },
    [setGroove, setInstrumentById, setStrum],
  );

  const isProgressionCustomized = React.useMemo(() => {
    if (!selectedMasterPreset) {
      return false;
    }

    const selectedPreset = getPresetById(selectedMasterPreset);
    const presetProgressionState = resolvePresetProgressionState(selectedPreset);
    return hasProgressionChanged(presetProgressionState, currentProgressionState);
  }, [selectedMasterPreset, currentProgressionState]);

  const handleProgressionStateChange = useCallback(
    (state: SequencerProgressionState) => {
      setCurrentProgressionState(state);
    },
    [],
  );

  // Auto-apply default preset on app load
  useEffect(() => {
    if (
      selectedMasterPreset !== "default" ||
      hasAppliedDefaultPresetRef.current
    ) {
      return;
    }

    hasAppliedDefaultPresetRef.current = true;

    const timeoutId = window.setTimeout(() => {
      handleMasterPresetChange("default");
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [handleMasterPresetChange, selectedMasterPreset]);

  const getSequencerSnapshot = useCallback(() => {
    return sequencerRef.current?.getSnapshot() ?? null;
  }, []);

  // Create state refs object for preset capture
  const stateRefs: CurrentStateRefs = {
    getCurrentSampleId: () => currentInstrument.id,
    getCurrentBpm: () => bpm,
    getCurrentKey: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.selectedKey ?? selectedKey;
    },
    getCurrentProgression: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.selectedProgression ?? selectedProgression;
    },
    getCurrentProgressionState: () => {
      const snapshot = getSequencerSnapshot();
      if (snapshot) {
        return snapshot.progressionState;
      }
      return {
        name: selectedProgression,
        roman: [],
        mode: "minor",
      };
    },
    getCurrentPatternPreset: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.patternPreset ?? "empty";
    },
    getCurrentPattern: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.pattern ?? [];
    },
    getCurrentStepsCount: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.stepsCount ?? 16;
    },
    getCurrentRegister: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.register ?? { bottomNote: "C3", topNote: "C4" };
    },
    getCurrentRibbonPositions: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.ribbonPositions ?? [];
    },
    getCurrentFxModes: () => fxModes,
    getCurrentSequencerMode: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.sequencerMode ?? "chord";
    },
    getCurrentDroneNotes: () => {
      const snapshot = getSequencerSnapshot();
      return snapshot?.droneNotes ?? [];
    },
    getCurrentStrum: () => getStrum(),
    getCurrentGroove: () => getGroove(),
  };

  return (
    <AppContainer id="app-container">
      <GlobalStyle />
      <Analytics />
      <Main id="app-main">
        {/* Master Preset Selector */}
        <MasterPresetSelector
          selectedPreset={selectedMasterPreset}
          onPresetChange={handleMasterPresetChange}
          isProgressionCustomized={isProgressionCustomized}
          bpm={bpm}
          onBpmChange={setBpm}
          stateRefs={stateRefs}
          isPlaying={isSequencerPlaying}
          onTogglePlay={handleTogglePlay}
          isSamplerLoaded={isSamplerLoaded}
        />

        <PluginWrapper id="plugin-wrapper">
          {waveformData.length > 0 && (
            <SampleAndFXSection
              currentInstrument={currentInstrument}
              waveformData={waveformData}
              sampleDuration={sampleDuration}
              onInstrumentChange={handleInstrumentChange}
              onPreviousInstrument={handlePreviousInstrument}
              onNextInstrument={handleNextInstrument}
              onShuffle={handleShuffle}
              mappingMode={mappingMode}
              fxModes={fxModes}
              onMappingModeChange={setMappingMode}
              onFxModeChange={setFxModes}
              onInitializeStepsWithGlobalValue={(fxType, globalValue) => {
                sequencerRef.current?.initializeStepsWithGlobalValue(
                  fxType,
                  globalValue,
                );
              }}
              hasMappingApplied={(fxType) => {
                return sequencerRef.current?.hasMappingApplied(fxType) ?? false;
              }}
              refreshTrigger={fxRefreshTrigger}
            />
          )}
          {error && <ErrorMessage id="error-message">{error}</ErrorMessage>}

          <RegisterProvider>
            <SequencerArea>
              <SequencerPerformanceContainer id="sequencer-performance-container">
                <PerformanceSection disabled={!isSamplerLoaded} />
                <Sequencer
                  ref={sequencerRef}
                  bpm={bpm}
                  sampler={sampler}
                  samplesMap={currentInstrument.samples}
                  onIsPlayingChange={handleIsPlayingChange}
                  onActiveNotesChange={setActiveKeyboardNotes}
                  onProgressionStateChange={handleProgressionStateChange}
                  disabled={!isSamplerLoaded}
                  mappingMode={mappingMode}
                  fxModes={fxModes}
                  onMappingModeChange={setMappingMode}
                />
                <MidiExportSection disabled={!isSamplerLoaded} />
              </SequencerPerformanceContainer>
              <RegisterKeyboardSection
                disabled={!isSamplerLoaded}
                activeNotes={activeKeyboardNotes}
                isSequencerPlaying={isSequencerPlaying}
              />
            </SequencerArea>
          </RegisterProvider>
        </PluginWrapper>
      </Main>
    </AppContainer>
  );
}

export default App;

// Styled Components
const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: "Dogica Pixel";
    src: url("/fonts/dogicapixel.ttf") format("truetype");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  body {
    margin: 0;
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  h1 {
    margin: 0 0 20px 0;
    font-size: 2rem;
  }
`;
const AppContainer = styled.div`
  text-align: center;
  background-color: black;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family:
    "Manrope",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  color: white;
`;

const Main = styled.main`
  flex: 1;
  padding: 20px;
  background-color: black;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const PluginWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(to bottom, #e2e1dd, #d8d4d3);
  border-radius: 8px;
  border: 1px solid #d8d4d3;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    opacity: 0.15;
    pointer-events: none;
    z-index: 1;
    border-radius: 8px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 10px;
  padding: 8px 15px;
  background-color: rgba(220, 38, 38, 0.1);
  color: #ef4444;
  border: 1px solid rgba(220, 38, 38, 0.2);
  border-radius: 4px;
  font-size: 0.875rem;
`;

const SequencerArea = styled.div.attrs({
  id: "sequencer-area-wrapper",
})`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
`;

const SequencerPerformanceContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  border-radius: 8px;
  padding: 16px 16px;
  position: relative;
  min-height: 0;

  .sequencer {
    flex: 1;
    min-width: 0;
  }
`;
